import React, { useState } from "react"
import { Input, Button, Flex, Typography } from "antd"
import { CheckCircleFilled, CloseOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import { validateDiscount } from "store/discounts"
import { computeDiscountAmount } from "utils/discountUtils"
import styles from "./index.module.css"

const { Text } = Typography

const PromoCodeInput = ({ totalAmount, addOnsTotal, appliedPromos, onPromoApplied, onPromoRemoved, experienceId, clientIdentifier }) => {
  const dispatch = useDispatch()
  const [code, setCode] = useState("")
  const [isValidating, setIsValidating] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const hasDiscount = appliedPromos.some((p) => p.type === "discount")

  const applyCode = async () => {
    const trimmedCode = code.trim().toUpperCase()
    if (!trimmedCode) return

    if (appliedPromos.some((p) => p.code === trimmedCode)) {
      setErrorMessage("This code has already been applied.")
      return
    }

    setIsValidating(true)
    setErrorMessage("")

    try {
      const result = await dispatch(validateDiscount(trimmedCode, totalAmount, experienceId, clientIdentifier, addOnsTotal))

      if (result.type === "discount" && hasDiscount) {
        setErrorMessage("Only one discount code can be applied per booking.")
        return
      }

      onPromoApplied({
        type: "discount",
        code: result.code ?? trimmedCode,
        name: result.name,
        discount_type: result.discount_type,
        value: Number(result.value),
        apply_to_add_ons: result.applicability_rules?.apply_to_add_ons ?? true,
        label: result.label ?? (result.discount_type === "percentage" ? `${result.value}% off` : `$${Number(result.value).toFixed(2)} off`)
      })
      setCode("")
    } catch (err) {
      const apiMessage = err?.data?.message?.base?.[0]
      setErrorMessage(apiMessage ?? "Invalid or expired code.")
    } finally {
      setIsValidating(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      applyCode()
    }
  }

  return (
    <div className={styles.wrapper}>
      {!hasDiscount && (
        <>
          <Flex gap={8}>
            <Input
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase())
                setErrorMessage("")
              }}
              onKeyDown={handleKeyDown}
              placeholder="Add promo code"
              size="large"
              disabled={isValidating}
              status={errorMessage ? "error" : ""}
            />
            <Button
              color="primary"
              variant="solid"
              size="large"
              onClick={applyCode}
              loading={isValidating}
              disabled={!code.trim()}
            >
              Apply
            </Button>
          </Flex>

          {errorMessage && <Text className={styles.errorText}>{errorMessage}</Text>}
        </>
      )}

      {appliedPromos.length > 0 && (
        <div className={styles.appliedSection}>
          <Text className={styles.appliedLabel}>Applied codes</Text>
          {appliedPromos.map((promo) => (
            <Flex key={promo.code} align="center" justify="space-between" className={styles.appliedItem}>
              <Flex align="center" gap={8}>
                <CheckCircleFilled className={styles.checkIcon} />
                <Flex vertical gap={0}>
                  <Text strong>{promo.code}</Text>
                  <Text className={styles.subText}>{promo.label}</Text>
                </Flex>
              </Flex>
              <Flex align="center" gap={8}>
                <Text className={styles.savingsText}>-${Number(computeDiscountAmount(promo, totalAmount, addOnsTotal)).toFixed(2)}</Text>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => onPromoRemoved(promo.code)}
                  className={styles.removeBtn}
                />
              </Flex>
            </Flex>
          ))}
        </div>
      )}
    </div>
  )
}

PromoCodeInput.propTypes = {
  totalAmount: PropTypes.number.isRequired,
  addOnsTotal: PropTypes.number,
  appliedPromos: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      type: PropTypes.string,
      discount_type: PropTypes.string,
      value: PropTypes.number,
      label: PropTypes.string
    })
  ).isRequired,
  onPromoApplied: PropTypes.func.isRequired,
  onPromoRemoved: PropTypes.func.isRequired,
  experienceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientIdentifier: PropTypes.string
}

PromoCodeInput.defaultProps = {
  addOnsTotal: 0
}

export default PromoCodeInput
